import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../common/Modal';
import './ModalForms.css';

const emptyForm = {
  operatorId: '',
  type: '',
  description: '',
  dateOut: '',
  dateIn: '',
  currency: '$',
  price: '',
  conversion: '',
  cost: '',
  bankExpense: '',
  bill: '',
  file: ''
};

const formatTwoDecimals = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return '';
  return parsed.toFixed(2);
};

const ServiceDetailsModal = ({ isOpen, onClose, onSave, initialData, operators, mode = 'full' }) => {
  const [formData, setFormData] = useState(emptyForm);
  const conversionDisabled = useMemo(() => formData.currency !== '$', [formData.currency]);
  const isDetailsMode = mode === 'details';
  const isSummaryMode = mode === 'summary';
  const isFullMode = !isDetailsMode && !isSummaryMode;

  const profitabilityLabel = useMemo(() => {
    const rawCost = formData.cost;
    const costNum = rawCost === '' || rawCost === null || rawCost === undefined ? null : Number(rawCost);
    if (costNum === null || Number.isNaN(costNum) || costNum === 0) return '-';

    const currency = formData.currency || '$';
    const price = Number(formData.price || 0);
    const rawBankExpense = formData.bankExpense;
    const bankExpenseNum = rawBankExpense === '' || rawBankExpense === null || rawBankExpense === undefined ? 0 : Number(rawBankExpense);
    const safeBankExpense = Number.isNaN(bankExpenseNum) ? 0 : bankExpenseNum;
    const value = price - costNum - safeBankExpense;
    return `${currency} ${value.toLocaleString('es-AR', { minimumFractionDigits: 2 })}`;
  }, [formData.currency, formData.price, formData.cost, formData.bankExpense]);

  useEffect(() => {
    if (!isOpen) return;

    if (initialData) {
      setFormData({
        operatorId: initialData.operator_id || initialData.operatorId || '',
        type: initialData.type || '',
        description: initialData.description || '',
        dateOut: initialData.date_out ? initialData.date_out.slice(0, 10) : (initialData.dateOut || ''),
        dateIn: initialData.date_in ? initialData.date_in.slice(0, 10) : (initialData.dateIn || ''),
        currency: initialData.currency || '$',
        conversion: formatTwoDecimals(initialData.conversion),
        price: initialData.price ?? '',
        cost: initialData.cost ?? '',
        bankExpense: initialData.bank_expense ?? initialData.bankExpense ?? '',
        bill: initialData.bill ?? '',
        file: initialData.file ?? ''
      });
    } else {
      setFormData(emptyForm);
    }
  }, [initialData, isOpen]);

  useEffect(() => {
    if (conversionDisabled) {
      setFormData((prev) => ({ ...prev, conversion: '' }));
    }
  }, [conversionDisabled]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    onSave({
      ...formData,
      conversion:
        formData.currency === '$' && formData.conversion !== ''
          ? Number(formatTwoDecimals(formData.conversion))
          : null
    });
  };

  const modalTitle = useMemo(() => {
    if (isDetailsMode) return 'Editar detalles del servicio';
    if (isSummaryMode) return 'Editar resumen del servicio';
    return initialData ? `Editar Servicio #${initialData.id}` : 'Editar servicio';
  }, [initialData, isDetailsMode, isSummaryMode]);

  return (
    <Modal
      title={modalTitle}
      isOpen={isOpen}
      onClose={onClose}
      onEnter={() => handleSubmit()}
      footer={(
        <div className="modal-footer">
          <div className="footer-left" />
          <div className="footer-actions">
            <button type="button" className="btn secondary" onClick={onClose}>Cancelar</button>
            <button type="submit" form={`service-details-modal-form-${mode}`} className="btn">Guardar</button>
          </div>
        </div>
      )}
    >
      <form id={`service-details-modal-form-${mode}`} className="modal-form" onSubmit={handleSubmit}>
        <div className="form-grid two-columns">
          {(isDetailsMode || isFullMode) && (
            <>
              <label>
                Fecha de salida
                <input type="date" name="dateIn" value={formData.dateIn} onChange={handleChange} disabled />
              </label>

              <label>
                Fecha de vuelta
                <input type="date" name="dateOut" value={formData.dateOut} onChange={handleChange} disabled />
              </label>

              <label style={{ gridColumn: '1 / -1' }}>
                Descripción
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  className="textarea-tall"
                  required
                />
              </label>

              <label>
                Tipo
                <select name="type" value={formData.type} onChange={handleChange} required>
                  <option value="">Selecciona</option>
                  <option value="Vuelo">Vuelo</option>
                  <option value="Hospedaje">Hospedaje</option>
                  <option value="Asistencia">Asistencia</option>
                  <option value="Paquete">Paquete</option>
                  <option value="Otro">Otro</option>
                </select>
              </label>

              <label>
                Operador
                <select name="operatorId" value={formData.operatorId} onChange={handleChange} required>
                  <option value="">Selecciona</option>
                  {(operators || []).map((op) => (
                    <option key={op.id} value={op.id}>{op.name}</option>
                  ))}
                </select>
              </label>
            </>
          )}

          {(isSummaryMode || isFullMode) && (
            <>
              <label style={{ gridColumn: '1 / -1' }}>
                File / Liquidación
                <input
                  name="file"
                  value={formData.file}
                  onChange={handleChange}
                  maxLength={512}
                  placeholder="Código de reserva del operador"
                />
              </label>

              <label>
                Precio
                <input type="number" step="0.01" name="price" value={formData.price} onChange={handleChange} required />
              </label>

              <label>
                Moneda
                <select name="currency" value={formData.currency} onChange={handleChange} required>
                  <option value="$">$</option>
                  <option value="USD">USD</option>
                </select>
              </label>

              <label>
                Conversión
                <input
                  type="number"
                  step="0.01"
                  name="conversion"
                  value={formData.conversion}
                  onChange={handleChange}
                  onBlur={(e) =>
                    setFormData((prev) => ({ ...prev, conversion: formatTwoDecimals(e.target.value) }))
                  }
                  disabled={conversionDisabled}
                  required={!conversionDisabled}
                  placeholder={conversionDisabled ? '—' : 'Tipo de cambio'}
                />
              </label>

              <label>
                Costo
                <input type="number" step="0.01" name="cost" value={formData.cost} onChange={handleChange} />
              </label>

              <label>
                Gasto Bancario
                <input type="number" step="0.01" name="bankExpense" value={formData.bankExpense} onChange={handleChange} />
              </label>

              <label>
                Rentabilidad
                <input value={profitabilityLabel} disabled />
              </label>

            </>
          )}

        </div>

      </form>
    </Modal>
  );
};

export default ServiceDetailsModal;

